import React from 'react'
import {
  Sidebar,
  SidebarProvider,
  SidebarInset,
  SidebarTrigger,
  SidebarContent,
  SidebarHeader,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuAction,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarSeparator,
  SidebarRail,
  useSidebar
} from '../../components/sidebar'
import { cn } from '../../lib/utils'

// Core Sidebar Components
export interface UISidebarProps extends Omit<React.ComponentProps<typeof Sidebar>, 'variant'> {
  variant?: 'default' | 'floating' | 'sidebar' | 'inset'
}

export interface UISidebarProviderProps extends React.ComponentProps<typeof SidebarProvider> {
  defaultOpen?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export interface UISidebarContentProps extends React.ComponentProps<typeof SidebarContent> {
  variant?: 'default' | 'scrollable' | 'compact'
}

export interface UISidebarHeaderProps extends React.ComponentProps<typeof SidebarHeader> {
  variant?: 'default' | 'compact' | 'elevated'
}

export interface UISidebarFooterProps extends React.ComponentProps<typeof SidebarFooter> {
  variant?: 'default' | 'compact' | 'actions'
}

// Menu System Components
export interface UISidebarMenuProps extends React.ComponentProps<typeof SidebarMenu> {
  variant?: 'default' | 'compact' | 'spacious'
}

export interface UISidebarMenuButtonProps extends Omit<React.ComponentProps<typeof SidebarMenuButton>, 'variant'> {
  variant?: 'default' | 'ghost' | 'subtle'
  isActive?: boolean
}

export interface UISidebarMenuItemProps extends React.ComponentProps<typeof SidebarMenuItem> {
  variant?: 'default' | 'compact'
}

export interface UISidebarMenuActionProps extends React.ComponentProps<typeof SidebarMenuAction> {
  variant?: 'default' | 'subtle'
}

// Group System Components
export interface UISidebarGroupProps extends React.ComponentProps<typeof SidebarGroup> {
  variant?: 'default' | 'section' | 'collapsible'
}

export interface UISidebarGroupLabelProps extends React.ComponentProps<typeof SidebarGroupLabel> {
  variant?: 'default' | 'section' | 'subtle'
}

/**
 * Sidebar component following design system best practices
 * Provides consistent sidebar styling and behavior
 */
export const UISidebar = React.forwardRef<
  React.ComponentRef<typeof Sidebar>,
  UISidebarProps
>(({ className, variant = 'default', ...props }, ref) => {
  return (
    <Sidebar
      ref={ref}
      className={cn(
        // Variant styles
        {
          '': variant === 'default',
          'border-none shadow-lg': variant === 'floating',
          'border-r border-border/40': variant === 'sidebar',
        },

        className
      )}
      {...props}
    />
  )
})

export const UISidebarProvider = React.forwardRef<
  React.ComponentRef<typeof SidebarProvider>,
  UISidebarProviderProps
>(({ defaultOpen = true, ...props }, ref) => {
  return (
    <SidebarProvider
      ref={ref}
      defaultOpen={defaultOpen}
      {...props}
    />
  )
})

export const UISidebarInset = React.forwardRef<
  React.ComponentRef<typeof SidebarInset>,
  React.ComponentProps<typeof SidebarInset>
>((props, ref) => {
  return (
    <SidebarInset
      ref={ref}
      {...props}
    />
  )
})

export const UISidebarTrigger = React.forwardRef<
  React.ComponentRef<typeof SidebarTrigger>,
  React.ComponentProps<typeof SidebarTrigger>
>((props, ref) => {
  return (
    <SidebarTrigger
      ref={ref}
      {...props}
    />
  )
})

export const UISidebarContent = React.forwardRef<
  React.ComponentRef<typeof SidebarContent>,
  UISidebarContentProps
>(({ className, variant = 'default', ...props }, ref) => {
  return (
    <SidebarContent
      ref={ref}
      className={cn(
        {
          'gap-2': variant === 'default',
          'gap-1 overflow-y-auto': variant === 'scrollable',
          'gap-1 py-2': variant === 'compact',
        },
        className
      )}
      {...props}
    />
  )
})

export const UISidebarHeader = React.forwardRef<
  React.ComponentRef<typeof SidebarHeader>,
  UISidebarHeaderProps
>(({ className, variant = 'default', ...props }, ref) => {
  return (
    <SidebarHeader
      ref={ref}
      className={cn(
        {
          'gap-2': variant === 'default',
          'gap-1 py-2': variant === 'compact',
          'gap-2 border-b border-border/40 pb-4': variant === 'elevated',
        },
        className
      )}
      {...props}
    />
  )
})

export const UISidebarFooter = React.forwardRef<
  React.ComponentRef<typeof SidebarFooter>,
  UISidebarFooterProps
>(({ className, variant = 'default', ...props }, ref) => {
  return (
    <SidebarFooter
      ref={ref}
      className={cn(
        {
          'gap-2': variant === 'default',
          'gap-1 py-2': variant === 'compact',
          'gap-2 border-t border-border/40 pt-4': variant === 'actions',
        },
        className
      )}
      {...props}
    />
  )
})

export const UISidebarMenu = React.forwardRef<
  React.ComponentRef<typeof SidebarMenu>,
  UISidebarMenuProps
>(({ className, variant = 'default', ...props }, ref) => {
  return (
    <SidebarMenu
      ref={ref}
      className={cn(
        {
          'gap-1': variant === 'default',
          'gap-0.5': variant === 'compact',
          'gap-2': variant === 'spacious',
        },
        className
      )}
      {...props}
    />
  )
})

export const UISidebarMenuButton = React.forwardRef<
  React.ComponentRef<typeof SidebarMenuButton>,
  UISidebarMenuButtonProps
>(({ className, variant = 'default', isActive, ...props }, ref) => {
  return (
    <SidebarMenuButton
      ref={ref}
      className={cn(
        // Base styles
        'transition-colors',

        // Variant styles
        {
          'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:bg-sidebar-accent focus-visible:text-sidebar-accent-foreground': variant === 'default',
          'hover:bg-transparent focus-visible:bg-transparent text-muted-foreground': variant === 'ghost',
          'hover:bg-sidebar-accent/50 focus-visible:bg-sidebar-accent/50': variant === 'subtle',
        },

        // Active state
        isActive && 'bg-sidebar-accent text-sidebar-accent-foreground',

        className
      )}
      {...props}
    />
  )
})

export const UISidebarMenuItem = React.forwardRef<
  React.ComponentRef<typeof SidebarMenuItem>,
  UISidebarMenuItemProps
>(({ className, variant = 'default', ...props }, ref) => {
  return (
    <SidebarMenuItem
      ref={ref}
      className={cn(
        {
          '': variant === 'default',
          'text-sm': variant === 'compact',
        },
        className
      )}
      {...props}
    />
  )
})

export const UISidebarMenuAction = React.forwardRef<
  React.ComponentRef<typeof SidebarMenuAction>,
  UISidebarMenuActionProps
>(({ className, variant = 'default', ...props }, ref) => {
  return (
    <SidebarMenuAction
      ref={ref}
      className={cn(
        'transition-colors',
        {
          'hover:bg-accent focus:bg-accent': variant === 'default',
          'hover:bg-accent/50 focus:bg-accent/50': variant === 'subtle',
        },
        className
      )}
      {...props}
    />
  )
})

export const UISidebarMenuSub = React.forwardRef<
  React.ComponentRef<typeof SidebarMenuSub>,
  React.ComponentProps<typeof SidebarMenuSub>
>((props, ref) => {
  return (
    <SidebarMenuSub
      ref={ref}
      {...props}
    />
  )
})

export const UISidebarMenuSubButton = React.forwardRef<
  React.ComponentRef<typeof SidebarMenuSubButton>,
  React.ComponentProps<typeof SidebarMenuSubButton>
>((props, ref) => {
  return (
    <SidebarMenuSubButton
      ref={ref}
      {...props}
    />
  )
})

export const UISidebarMenuSubItem = React.forwardRef<
  React.ComponentRef<typeof SidebarMenuSubItem>,
  React.ComponentProps<typeof SidebarMenuSubItem>
>((props, ref) => {
  return (
    <SidebarMenuSubItem
      ref={ref}
      {...props}
    />
  )
})

export const UISidebarGroup = React.forwardRef<
  React.ComponentRef<typeof SidebarGroup>,
  UISidebarGroupProps
>(({ className, variant = 'default', ...props }, ref) => {
  return (
    <SidebarGroup
      ref={ref}
      className={cn(
        {
          '': variant === 'default',
          'border-b border-border/40 pb-4 mb-4': variant === 'section',
          'rounded-lg border border-border/40 p-2': variant === 'collapsible',
        },
        className
      )}
      {...props}
    />
  )
})

export const UISidebarGroupLabel = React.forwardRef<
  React.ComponentRef<typeof SidebarGroupLabel>,
  UISidebarGroupLabelProps
>(({ className, variant = 'default', ...props }, ref) => {
  return (
    <SidebarGroupLabel
      ref={ref}
      className={cn(
        {
          'text-xs font-medium text-muted-foreground': variant === 'default',
          'text-sm font-semibold text-foreground border-b border-border/40 pb-1': variant === 'section',
          'text-xs font-medium text-muted-foreground/70': variant === 'subtle',
        },
        className
      )}
      {...props}
    />
  )
})

export const UISidebarGroupContent = React.forwardRef<
  React.ComponentRef<typeof SidebarGroupContent>,
  React.ComponentProps<typeof SidebarGroupContent>
>((props, ref) => {
  return (
    <SidebarGroupContent
      ref={ref}
      {...props}
    />
  )
})

export const UISidebarSeparator = React.forwardRef<
  React.ComponentRef<typeof SidebarSeparator>,
  React.ComponentProps<typeof SidebarSeparator>
>((props, ref) => {
  return <SidebarSeparator ref={ref} {...props} />
})

export const UISidebarRail = React.forwardRef<
  React.ComponentRef<typeof SidebarRail>,
  React.ComponentProps<typeof SidebarRail>
>((props, ref) => {
  return <SidebarRail ref={ref} {...props} />
})

// Display names
UISidebar.displayName = "UISidebar"
UISidebarProvider.displayName = "UISidebarProvider"
UISidebarInset.displayName = "UISidebarInset"
UISidebarTrigger.displayName = "UISidebarTrigger"
UISidebarContent.displayName = "UISidebarContent"
UISidebarHeader.displayName = "UISidebarHeader"
UISidebarFooter.displayName = "UISidebarFooter"
UISidebarMenu.displayName = "UISidebarMenu"
UISidebarMenuButton.displayName = "UISidebarMenuButton"
UISidebarMenuItem.displayName = "UISidebarMenuItem"
UISidebarMenuAction.displayName = "UISidebarMenuAction"
UISidebarMenuSub.displayName = "UISidebarMenuSub"
UISidebarMenuSubButton.displayName = "UISidebarMenuSubButton"
UISidebarMenuSubItem.displayName = "UISidebarMenuSubItem"
UISidebarGroup.displayName = "UISidebarGroup"
UISidebarGroupLabel.displayName = "UISidebarGroupLabel"
UISidebarGroupContent.displayName = "UISidebarGroupContent"
UISidebarSeparator.displayName = "UISidebarSeparator"
UISidebarRail.displayName = "UISidebarRail"

// Re-export hook
export { useSidebar }